import { inject, Injectable } from "@angular/core";
import { BookingPrescriptionStateService } from "../state/booking-prescription-state.service";
import { GoogleCalendarEventsApiService } from "../api/calendar/google-calendar-events-api.service";
import { GoogleEventsMapperService } from "../mappers/google-events-mapper.service";
import { catchError, Observable, tap, throwError } from "rxjs";
import { CreateEventDto, UpdateEventDto } from "../../api/models";
import { BookingPrescriptionState } from "../../interfaces/booking-prescription-state.interface";

@Injectable({
  providedIn: "root",
})
export class ProcessPrescriptionService {
  /** The booking prescription state service instance */
  private readonly _bookingPrescriptionStateService: BookingPrescriptionStateService =
    inject(BookingPrescriptionStateService);

  /** API service for managing Google Calendar events */
  private readonly _googleCalendarApi: GoogleCalendarEventsApiService = inject(
    GoogleCalendarEventsApiService,
  );

  /** Maps internal state data to Google Calendar event DTOs */
  private readonly _googleEventsMapper: GoogleEventsMapperService = inject(
    GoogleEventsMapperService,
  );

  /**
   * Processes the prescription based on the current booking state and action type.
   * Maps the state to a Google Calendar event DTO and triggers the appropriate API call.
   * Sets the loading state during the operation.
   *
   * @returns {void}
   */
  public processPrescription(): void {
    const state = this._bookingPrescriptionStateService.state();
    const action = this._bookingPrescriptionStateService.getActionType();
    const { dto } = this._googleEventsMapper.mapToEventDto(
      state,
      action,
      "test",
    );

    this._processByAction$(state, action, dto).subscribe(() => {
      this._bookingPrescriptionStateService.setCompletedProcess(true);
    });
  }

  /**
   * Executes the appropriate Google Calendar API operation (create or update) based on the action type.
   * Handles missing parameters and error propagation.
   *
   * @param {BookingPrescriptionState} state - The current booking prescription state.
   * @param {"create" | "update"} action - The action type to perform.
   * @param {CreateEventDto | UpdateEventDto} dto - The data transfer object for the event.
   * @returns {Observable<void>} Observable that completes when the operation is done.
   */
  private _processByAction$(
    state: BookingPrescriptionState,
    action: "create" | "update",
    dto: CreateEventDto | UpdateEventDto,
  ): Observable<void> {
    const operation$ =
      action === "create"
        ? this._createEvent$(dto as CreateEventDto)
        : this._updateEvent$(state, dto as UpdateEventDto);

    return operation$.pipe(
      catchError((error) => {
        const errorMsg = `Error in consultation ${action}`;
        console.error(errorMsg, error);
        this._bookingPrescriptionStateService.setError(error);
        return throwError(() => new Error(`${errorMsg}: ${error.message}`));
      }),
    );
  }

  /**
   * Calls the Google Calendar API to create a new event.
   * @param dto The data transfer object for creating the event
   * @returns {Observable<void>} Observable that completes when the event is created
   */
  private _createEvent$(dto: CreateEventDto): Observable<void> {
    return this._googleCalendarApi.create$(dto as CreateEventDto);
  }

  /**
   * Calls the Google Calendar API to update an existing event.
   * Checks for required parameters in the state before proceeding.
   * @param state The current booking prescription state
   * @param dto The data transfer object for updating the event
   * @returns {Observable<void>} Observable that completes when the event is updated, or errors if parameters are missing
   */
  private _updateEvent$(
    state: BookingPrescriptionState,
    dto: UpdateEventDto,
  ): Observable<void> {
    const { clinicEmail, calendarId, testId } = state;

    if (!clinicEmail || !calendarId || !testId) {
      return throwError(
        () => new Error("Missing parameters for update operation"),
      );
    }

    return this._googleCalendarApi.update$(
      clinicEmail,
      calendarId,
      testId,
      dto as UpdateEventDto,
    );
  }
}
